import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableCell, TableRow, TableSortLabel,
    IconButton, Tooltip,
    Box, LinearProgress,
    Snackbar, Alert,
    Chip,
    TextField, MenuItem,
} from '@mui/material';
import { PointOfSale, Visibility, Download } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Loader } from '../../../shared/components/Loader';
import { invoiceService } from '../../../shared/services/invoiceService';
import { Invoice } from '../../../shared/types/invoice';

const fmtDate = (d?: string) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

const fmtAmount = (n: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n ?? 0);

export const SalesList: React.FC = () => {
    const navigate = useNavigate();

    const [sales, setSales] = useState<Invoice[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });
    const [pendingFilters, setPendingFilters] = useState({ client: '', dateFrom: '', dateTo: '' });
    const [appliedFilters, setAppliedFilters] = useState({ client: '', dateFrom: '', dateTo: '' });

    const fetchSales = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await invoiceService.getSales({ page: 1, limit: 10000 });
            const outerData = res?.data;
            const items: Invoice[] = outerData?.data || outerData?.items || (outerData as any)?.invoices || [];
            setSales(
                (Array.isArray(items) ? items : []).map((inv: any) => ({
                    ...inv,
                    _id: inv._id ?? inv.id,
                    clientIdStr: typeof inv.clientId === 'object'
                        ? (inv.clientId?._id || inv.clientId?.id || '')
                        : (inv.clientId || ''),
                    clientName:
                        typeof inv.clientId === 'object'
                            ? (inv.clientId?.displayName || inv.clientId?.companyName || inv.clientId?.ownerName)
                            : inv.clientCompanyName,
                }))
            );
        } catch {
            setSnackbar({ open: true, message: 'Error loading sales', severity: 'error' });
            setSales([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchSales(); }, [fetchSales]);

    const uniqueClients = React.useMemo(() => {
        const seen = new Set<string>();
        return sales.filter(inv => {
            const id = (inv as any).clientIdStr;
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
        }).map(inv => ({ id: (inv as any).clientIdStr as string, name: (inv as any).clientName as string }));
    }, [sales]);

    const filteredSales = React.useMemo(() => {
        let list = [...sales];
        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            list = list.filter(inv => {
                const clientName = (inv as any).clientName;
                return (
                    String(inv.invoiceNumber || '').toLowerCase().includes(q) ||
                    String(clientName || '').toLowerCase().includes(q)
                );
            });
        }

        if (appliedFilters.client) {
            list = list.filter(inv => (inv as any).clientIdStr === appliedFilters.client);
        }
        if (appliedFilters.dateFrom) {
            const from = new Date(appliedFilters.dateFrom).getTime();
            list = list.filter(inv => inv.invoiceDate && new Date(inv.invoiceDate).getTime() >= from);
        }
        if (appliedFilters.dateTo) {
            const to = new Date(appliedFilters.dateTo).setHours(23, 59, 59, 999);
            list = list.filter(inv => inv.invoiceDate && new Date(inv.invoiceDate).getTime() <= to);
        }

        if (sortData?.active) {
            list.sort((a, b) => {
                const av = (a as any)[sortData.active] ?? '';
                const bv = (b as any)[sortData.active] ?? '';
                if (typeof av === 'string' && typeof bv === 'string')
                    return sortData.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                if (av < bv) return sortData.direction === 'asc' ? -1 : 1;
                if (av > bv) return sortData.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return list;
    }, [sales, searchInput, sortData, appliedFilters]);

    const paginatedSales = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredSales.slice(start, start + pageSize);
    }, [filteredSales, currentPage, pageSize]);

    const totalSalesAmount = React.useMemo(() =>
        filteredSales.reduce((sum, inv) => sum + (inv.totalAmount ?? 0), 0),
        [filteredSales]
    );

    const handleSort = (col: string) => {
        setSortData(prev =>
            prev?.active === col
                ? { active: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { active: col, direction: 'asc' }
        );
    };

    const handleExport = () => {
        const headers = ['Invoice Number', 'Client Company', 'Total Amount', 'Invoice Date'];
        const rows = filteredSales.map(inv => {
            const clientName = (inv as any).clientName;
            return [inv.invoiceNumber, clientName || '', inv.totalAmount, fmtDate(inv.invoiceDate)];
        });
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sales.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const SortableHeader = ({ col, label }: { col: string; label: string }) => (
        <TableCell className="!font-semibold !text-gray-700">
            <TableSortLabel
                active={sortData?.active === col}
                direction={sortData?.active === col ? sortData.direction : 'asc'}
                onClick={() => handleSort(col)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const handleApplyFilters = () => {
        setAppliedFilters(pendingFilters);
        setCurrentPage(0);
    };

    const handleResetFilters = () => {
        setPendingFilters({ client: '', dateFrom: '', dateTo: '' });
        setAppliedFilters({ client: '', dateFrom: '', dateTo: '' });
        setCurrentPage(0);
    };

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <PointOfSale className="text-[#6366f1] w-8 h-8" />
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Sales
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={(v) => { setSearchInput(v); setCurrentPage(0); }}
                        onSearch={() => setCurrentPage(0)}
                        placeholder="Search sales..."
                        className="w-full sm:w-64"
                    />
                </div>
            </div>

            {/* Action Bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 sm:px-10 py-2 border-b border-gray-100">
                <div className="flex gap-2">
                    <Tooltip title="Export Sales Report">
                        <AppButton
                            variant="primary"
                            onClick={handleExport}
                            sx={{ borderRadius: '8px', backgroundColor: '#0EA5E9', '&:hover': { backgroundColor: '#0284C7' }, px: 2, gap: 1 }}
                        >
                            <Download fontSize="small" />
                            Export Sales Report
                        </AppButton>
                    </Tooltip>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <AppButton
                        variant="primary"
                        disableRipple
                        sx={{ backgroundColor: '#22c55e', '&:hover': { backgroundColor: '#22c55e' }, cursor: 'default', px: 2, py: 1, borderRadius: '6px', minWidth: 240 }}
                    >
                        Total Sales Amount : {fmtAmount(totalSalesAmount)}
                    </AppButton>
                </div>
            </div>

            {/* Table */}
            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-x-auto">

                {/* Filters Panel */}
                <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5 mb-6">
                    <div className="mb-4">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#6366f1]">Filters</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <TextField
                            select
                            label="Client"
                            value={pendingFilters.client}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, client: e.target.value }))}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="">All Clients</MenuItem>
                            {uniqueClients.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Date From"
                            type="date"
                            value={pendingFilters.dateFrom}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            label="Date To"
                            type="date"
                            value={pendingFilters.dateTo}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                    <div className="flex gap-2 mt-4">
                        <AppButton variant="primary" onClick={handleApplyFilters} sx={{ px: 3, py: 1, borderRadius: '6px' }}>
                            Search
                        </AppButton>
                        <AppButton variant="secondary" onClick={handleResetFilters} sx={{ px: 3, py: 1, borderRadius: '6px' }}>
                            Reset
                        </AppButton>
                    </div>
                </div>

                <div className="flex flex-col w-full flex-1">
                    {isLoading && sales.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && sales.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <SortableHeader col="invoiceNumber" label="Invoice Number" />
                                    <SortableHeader col="clientName" label="Client" />
                                    <SortableHeader col="totalAmount" label="Total Amount" />
                                    <SortableHeader col="invoiceDate" label="Invoice Date" />
                                    <TableCell className="!font-semibold !text-gray-700">Type</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredSales.length === 0}
                            emptyColSpan={7}
                        >
                            {paginatedSales.map((inv, i) => {
                                const clientName = (inv as any).clientName;
                                return (
                                    <TableRow key={inv._id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <TableCell className="!text-gray-500 !text-sm">
                                            {currentPage * pageSize + i + 1}
                                        </TableCell>
                                        <TableCell className="!text-gray-900 !font-medium">
                                            {inv.invoiceNumber}
                                        </TableCell>
                                        <TableCell className="!text-gray-700">{clientName || '—'}</TableCell>
                                        <TableCell className="!text-gray-700 !font-semibold">
                                            {fmtAmount(inv.totalAmount)}
                                        </TableCell>
                                        <TableCell className="!text-gray-600">{fmtDate(inv.invoiceDate)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={inv.type || inv.invoiceType || 'INV'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: (inv.type || inv.invoiceType) === 'CF' ? '#f0fdf4' : '#eff6ff',
                                                    color: (inv.type || inv.invoiceType) === 'CF' ? '#16a34a' : '#2563eb',
                                                    fontWeight: 600,
                                                    fontSize: '11px',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View">
                                                <IconButton
                                                    onClick={() =>
                                                        navigate(`/invoices/view/${inv._id}`, {
                                                            state: { backTo: '/invoices/sales' },
                                                        })
                                                    }
                                                    size="small"
                                                    className="text-[#0EA5E9] hover:bg-blue-50"
                                                >
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredSales.length}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={(_, p) => setCurrentPage(p - 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
                />
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity as any} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
